"use client";

import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import { Calendar } from "@baluu/ui/components/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@baluu/ui/components/popover";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Covers any living attendee without listing centuries of dead years. */
const DOB_YEARS_BACK = 120;
/** Opening month for an empty birth date — the common case is a child. */
const DOB_DEFAULT_YEARS_BACK = 8;

/**
 * Parse the stored `YYYY-MM-DD` contract as a LOCAL calendar date.
 *
 * Local, not UTC: this value is a calendar date, and the calendar renders in
 * the viewer's own days. `new Date("2018-03-04")` would parse as UTC midnight
 * and render as 3 March for anyone west of Greenwich.
 */
export function parseDateValue(value: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!m) return undefined;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(year, month - 1, day);
  // Reject impossible dates rather than letting them roll over: new Date(2018,
  // 1, 30) silently becomes 2 March.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return date;
}

/**
 * Format for display — always "21 Aug 2026", never a numeric order.
 *
 * A native `<input type="date">` follows the BROWSER locale, so a UK school's
 * checkout rendered a child's birth date as 08/21/2026. A written month is
 * unambiguous everywhere. Returns "" for empty/malformed input so a bad stored
 * value degrades to the placeholder instead of throwing.
 */
export function formatDateValue(value: string): string {
  const date = parseDateValue(value);
  if (!date) return "";
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Serialize back to the stored contract, in local calendar terms. */
function toValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export type DateFieldProps = {
  /** `YYYY-MM-DD`, the shape the API stores. */
  value: string;
  onChange: (value: string) => void;
  /** `dateOfBirth` disables today and every future date. */
  mode?: "date" | "dateOfBirth";
  /**
   * Render the popover inline instead of portalling to `document.body`.
   * Required inside the webcomponents Shadow DOM, where a body portal lands
   * outside the shadow root and is invisible — the same reason the checkout
   * Selects pass `container={null}`.
   */
  inShadowDom?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
};

export function DateField({
  value,
  onChange,
  mode = "date",
  inShadowDom = false,
  disabled = false,
  placeholder = "Select a date",
  className,
  id,
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseDateValue(value);
  const isDob = mode === "dateOfBirth";

  // Midnight today, so "future" comparisons are day-granular.
  const today = React.useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const { startMonth, endMonth, defaultMonth, disabledDays } = React.useMemo(() => {
    if (!isDob) {
      // Bounds are explicit even for a plain date. With captionLayout
      // "dropdown" and no endMonth, react-day-picker defaults navEnd to
      // endOfYear(today) (helpers/getNavMonth.js), which would silently make
      // any future year unreachable — a regression versus the native input
      // this replaced. Range matches the widget's existing date picker.
      return {
        startMonth: new Date(today.getFullYear() - 100, 0, 1),
        endMonth: new Date(today.getFullYear() + 10, 11, 31),
        defaultMonth: selected,
        disabledDays: undefined,
      };
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      startMonth: new Date(today.getFullYear() - DOB_YEARS_BACK, 0, 1),
      // Ending at today keeps the year dropdown from offering years that can
      // only produce a value the server will reject.
      endMonth: today,
      // Open on the selected date, else eight years back — saves scrolling
      // from the current year for what is usually a child.
      defaultMonth:
        selected ?? new Date(today.getFullYear() - DOB_DEFAULT_YEARS_BACK, 0, 1),
      // "after yesterday" disables today AND everything later.
      disabledDays: { after: yesterday },
    };
  }, [isDob, selected, today]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {formatDateValue(value) || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 z-[10002]"
        align="start"
        {...(inShadowDom ? { container: null } : {})}
      >
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={defaultMonth}
          startMonth={startMonth}
          endMonth={endMonth}
          // Month and year as dropdowns — a birth year is otherwise a long
          // click-through from today.
          captionLayout="dropdown"
          disabled={disabledDays}
          onSelect={(date: Date | undefined) => {
            // Clicking the selected day deselects it — emit "" so an optional
            // field can be emptied again. Swallowing the undefined would make
            // a value permanent once set, which the native input it replaced
            // never did.
            onChange(date ? toValue(date) : "");
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
